import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { GRID, LAHETE } from '@/constants/testIds';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CheckCircle2, Circle, Printer, StickyNote } from 'lucide-react';
import { PresenceDot } from '@/components/PresenceAvatar';
import AuditPopover from '@/components/AuditPopover';
import { downloadPdf } from '@/lib/api';
import { toast } from 'sonner';

// Box size in kg — Tammilehdon Herneet sell ONE product (10 kg pea boxes).
// All Määrä values must be a multiple of BOX_KG; commit snaps to the
// nearest multiple and surfaces a toast if rounding occurred.
const BOX_KG = 10;
// Status options the user is allowed to set on an order. Anything else
// (free text) is rejected on commit and the input reverts to the
// previous value. Empty string clears the status.
const STATUS_ALLOWED = ['Arvio', 'Tilaus', 'Laskutettu'];

function statusColor(s) {
  if (s === 'Tilaus') return 'bg-[#E8F0E9] text-[#2A4B30]';
  if (s === 'Arvio') return 'bg-[#FDF6EB] text-[#8A6A1F]';
  if (s === 'Laskutettu') return 'bg-[#E3E8F5] text-[#2B3F7E]';
  return 'bg-transparent text-[#6A6D66]';
}

/* --- Reusable inline cells --- */

function InlineNumberCell({ value, onCommit, testId, onEditingChange, onEnterAdvance, registerRef }) {
  const initial = value == null ? '' : String(value);
  const [v, setV] = useState(initial);
  const original = useRef(initial);
  const focused = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => { if (registerRef) registerRef(inputRef); }, [registerRef]);

  useEffect(() => {
    const next = value == null ? '' : String(value);
    if (!focused.current && next !== original.current) {
      setV(next);
      original.current = next;
    }
  }, [value]);

  const [hasError, setHasError] = useState(false);
  const errorTimeoutRef = useRef(null);

  useEffect(() => () => { if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current); }, []);

  const flashError = () => {
    setHasError(true);
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    errorTimeoutRef.current = setTimeout(() => setHasError(false), 2500);
  };

  const commit = () => {
    focused.current = false;
    if (onEditingChange) onEditingChange(false);
    if (v === original.current) return;
    const num = v === '' ? 0 : Number(v.replace(',', '.'));
    if (Number.isNaN(num)) {
      toast.error(`"${v}" ei ole kelvollinen luku`);
      setV(original.current);
      flashError();
      return;
    }
    if (num < 0) {
      toast.error('Määrä ei voi olla negatiivinen');
      setV(original.current);
      flashError();
      return;
    }
    // Strict validation — only exact 10 kg multiples are accepted.
    // We DON'T snap silently any more; invalid values revert and flash red.
    if (num > 0 && num % BOX_KG !== 0) {
      toast.error(
        `${num} kg ei kelpaa — määrä on syötettävä 10 kg:n kerrannaisena (1 laatikko = 10 kg). Esim. 10, 20, 30, 100…`,
        { duration: 5000 },
      );
      setV(original.current);
      flashError();
      return;
    }
    setV(String(num));
    if (String(num) === original.current) return;
    original.current = String(num);
    onCommit(num);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={v}
      onChange={(e) => { setV(e.target.value); if (hasError) setHasError(false); }}
      onFocus={(e) => { focused.current = true; if (onEditingChange) onEditingChange(true); e.currentTarget.select(); }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
          if (onEnterAdvance) onEnterAdvance(); else e.currentTarget.blur();
        }
        if (e.key === 'Escape') { setV(original.current); setHasError(false); e.currentTarget.blur(); }
      }}
      data-testid={testId}
      aria-invalid={hasError || undefined}
      title={hasError ? 'Määrän tulee olla 10 kg:n kerrannainen (1 laatikko = 10 kg)' : undefined}
      className={`font-mono w-full bg-transparent border focus:ring-1 focus:outline-none rounded px-2 py-1 text-right transition-all ${
        hasError
          ? 'border-red-500 ring-1 ring-red-500 bg-red-50 text-red-700 animate-pulse'
          : 'border-transparent hover:border-[#E5E6E3] focus:border-[#2A4B30] focus:ring-[#2A4B30]'
      }`}
    />
  );
}

function StatusAutocompleteCell({ value, onCommit, testId, options, onEditingChange, onEnterAdvance, registerRef }) {
  // Only the canonical allowed statuses are settable. We accept the reference
  // list from props but intersect with STATUS_ALLOWED so future reference
  // changes never relax client-side validation.
  const allowed = useMemo(
    () => (options || []).filter((o) => STATUS_ALLOWED.includes(o)),
    [options],
  );
  // Ensure the canonical order even if backend reorders them.
  const ordered = useMemo(
    () => STATUS_ALLOWED.filter((o) => allowed.includes(o) || allowed.length === 0),
    [allowed],
  );

  const initial = value || '';
  const [v, setV] = useState(initial);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const original = useRef(initial);
  const focused = useRef(false);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => { if (registerRef) registerRef(inputRef); }, [registerRef]);

  useEffect(() => {
    const next = value || '';
    if (!focused.current && next !== original.current) {
      setV(next); original.current = next;
    }
  }, [value]);

  // Suggestions = options whose start (case-insensitive) matches typed text.
  // Empty input → show all options.
  const suggestions = useMemo(() => {
    const t = (v || '').trim().toLowerCase();
    if (!t) return ordered;
    const prefix = ordered.filter((o) => o.toLowerCase().startsWith(t));
    if (prefix.length > 0) return prefix;
    // Fallback to substring so user can search by middle chars
    return ordered.filter((o) => o.toLowerCase().includes(t));
  }, [v, ordered]);

  useEffect(() => {
    // Reset highlight whenever suggestions change so it stays valid
    setHighlight(0);
  }, [suggestions.length]);

  const commitValue = (raw, opts = {}) => {
    focused.current = false;
    if (onEditingChange) onEditingChange(false);
    const wasOpen = open;
    setOpen(false);
    const trimmed = (raw == null ? '' : String(raw)).trim();

    // If the dropdown is open and the user has navigated with the keyboard,
    // we prefer the highlighted suggestion over the typed text. We treat the
    // suggestion as "highlighted" only when (a) there's >1 suggestion and the
    // user moved off zero OR (b) the user is committing an empty input but
    // has a highlight to honour.
    if (wasOpen && opts.preferHighlight !== false && suggestions.length > 0) {
      const picked = suggestions[Math.min(highlight, suggestions.length - 1)];
      if (picked) {
        // When the input is empty, only honor highlight if the user moved it
        // (highlight > 0) — otherwise empty + Enter still means "clear".
        const userMoved = highlight > 0;
        const exact = (trimmed && picked.toLowerCase() === trimmed.toLowerCase());
        const prefix = (trimmed && picked.toLowerCase().startsWith(trimmed.toLowerCase()));
        if (exact || prefix || (trimmed === '' && userMoved)) {
          setV(picked);
          if (picked !== original.current) {
            original.current = picked;
            onCommit(picked);
          }
          return;
        }
      }
    }

    if (trimmed === '') {
      setV('');
      if (original.current !== '') {
        original.current = '';
        onCommit('');
      }
      return;
    }
    // Exact match wins
    const exact = ordered.find((o) => o.toLowerCase() === trimmed.toLowerCase());
    if (exact) {
      setV(exact);
      if (exact !== original.current) {
        original.current = exact;
        onCommit(exact);
      }
      return;
    }
    // No exact match → take the currently highlighted suggestion if any
    if (suggestions.length > 0) {
      const picked = suggestions[Math.min(highlight, suggestions.length - 1)];
      setV(picked);
      if (picked !== original.current) {
        original.current = picked;
        onCommit(picked);
      }
      return;
    }
    // Nothing matched — silently revert. We do NOT save invalid free text.
    setV(original.current);
    toast.error(`Status hyväksyy vain: ${STATUS_ALLOWED.join(', ')}`);
  };

  const handleEnter = () => {
    commitValue(v);
    if (onEnterAdvance) onEnterAdvance(); else if (inputRef.current) inputRef.current.blur();
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={v}
        placeholder="—"
        onChange={(e) => { setV(e.target.value); setOpen(true); }}
        onFocus={(e) => {
          focused.current = true;
          if (onEditingChange) onEditingChange(true);
          setOpen(true);
          e.currentTarget.select();
        }}
        onBlur={() => {
          // Defer to allow click on suggestion to fire first
          setTimeout(() => {
            if (!focused.current) return;
            commitValue(v);
          }, 120);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleEnter();
            return;
          }
          if (e.key === 'Tab') {
            // Let Tab move focus, but commit value first
            commitValue(v);
            return;
          }
          if (e.key === 'Escape') {
            focused.current = false;
            if (onEditingChange) onEditingChange(false);
            setV(original.current);
            setOpen(false);
            e.currentTarget.blur();
            return;
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setHighlight((h) => Math.min(h + 1, Math.max(0, suggestions.length - 1)));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
            return;
          }
        }}
        data-testid={testId}
        autoComplete="off"
        className={`w-full border border-transparent hover:border-[#E5E6E3] focus:border-[#2A4B30] focus:ring-1 focus:ring-[#2A4B30] focus:outline-none rounded px-2 py-1 text-sm transition-all ${statusColor(v)}`}
      />
      {open && suggestions.length > 0 && (
        <ul
          data-testid={testId ? `${testId}-suggestions` : undefined}
          className="absolute z-30 mt-0.5 left-0 w-full min-w-[7rem] bg-white border border-[#E5E6E3] rounded-md shadow-lg py-1 text-sm"
          // Keep mouse-down from blurring the input before we commit the click
          onMouseDown={(e) => e.preventDefault()}
        >
          {suggestions.map((opt, idx) => {
            const active = idx === highlight;
            return (
              <li
                key={opt}
                data-testid={testId ? `${testId}-opt-${opt}` : undefined}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => {
                  commitValue(opt);
                  if (onEnterAdvance) onEnterAdvance();
                }}
                className={`px-2.5 py-1 cursor-pointer flex items-center gap-2 ${
                  active ? 'bg-[#E8F0E9] text-[#1E3622]' : 'text-[#1C1C1A] hover:bg-[#F4F6F3]'
                }`}
              >
                <span className={`inline-block w-2 h-2 rounded-full ${
                  opt === 'Tilaus' ? 'bg-[#3B6E44]' :
                  opt === 'Arvio' ? 'bg-[#C28D2F]' :
                  opt === 'Laskutettu' ? 'bg-[#4A6BBE]' : 'bg-[#A0A39C]'
                }`} />
                {opt}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function LaheteCheckboxCell({ value, onCommit, testId, registerRef, onEnterAdvance }) {
  const isChecked = !!(value && String(value).trim());
  const inputRef = useRef(null);
  useEffect(() => { if (registerRef) registerRef(inputRef); }, [registerRef]);

  const toggle = () => onCommit(isChecked ? '' : 'Tehty');

  return (
    <label
      className={`inline-flex items-center gap-1.5 cursor-pointer select-none px-1.5 py-1 rounded hover:bg-[#F9F9F6] focus-within:ring-1 focus-within:ring-[#2A4B30] focus-within:ring-offset-1 ${isChecked ? 'text-[#2A4B30]' : 'text-[#6A6D66]'}`}
    >
      <input
        ref={inputRef}
        type="checkbox"
        checked={isChecked}
        onChange={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (onEnterAdvance) onEnterAdvance();
          }
        }}
        data-testid={testId}
        className="sr-only"
      />
      {isChecked ? (
        <CheckCircle2 size={15} strokeWidth={2.2} className="text-[#3B6E44]" />
      ) : (
        <Circle size={15} strokeWidth={1.6} />
      )}
      <span className="text-xs">{isChecked ? 'Tehty' : 'Tehdään'}</span>
    </label>
  );
}

function InlineTextCell({ value, onCommit, testId, placeholder = '', onEditingChange, registerRef }) {
  const initial = value || '';
  const [v, setV] = useState(initial);
  const original = useRef(initial);
  const focused = useRef(false);
  const inputRef = useRef(null);
  useEffect(() => { if (registerRef) registerRef(inputRef); }, [registerRef]);

  useEffect(() => {
    const next = value || '';
    if (!focused.current && next !== original.current) { setV(next); original.current = next; }
  }, [value]);

  const commit = () => {
    focused.current = false;
    if (onEditingChange) onEditingChange(false);
    if (v === original.current) return;
    original.current = v;
    onCommit(v);
  };
  return (
    <input
      ref={inputRef}
      type="text"
      value={v}
      placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onFocus={() => { focused.current = true; if (onEditingChange) onEditingChange(true); }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); }
        if (e.key === 'Escape') { setV(original.current); e.currentTarget.blur(); }
      }}
      data-testid={testId}
      className="w-full bg-transparent border border-transparent hover:border-[#E5E6E3] focus:border-[#2A4B30] focus:ring-1 focus:ring-[#2A4B30] focus:outline-none rounded px-2 py-1 transition-all"
    />
  );
}

/* --- Delivery note popover (per-order ad-hoc instructions) --- */

function DeliveryNotePopover({ value, customerNote, onCommit, testId, disabled }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const hasOrderNote = (value || '').trim().length > 0;
  const hasCustomerNote = (customerNote || '').trim().length > 0;
  const anyNote = hasOrderNote || hasCustomerNote;

  const save = () => {
    const next = draft.trim();
    if (next !== (value || '').trim()) onCommit(next);
    setOpen(false);
  };

  if (disabled) {
    // Hide the trigger entirely on 0-kg rows — there's no lähete to attach
    // a note to anyway.
    return <span className="w-7 h-7 inline-block" aria-hidden="true" />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          title={
            hasOrderNote
              ? `Tilauskohtainen lisätieto:\n${value}`
              : hasCustomerNote
                ? `Asiakaskohtainen toimitusohje:\n${customerNote}\n\nKlikkaa lisätäksesi tilauskohtainen huomio.`
                : 'Lisää lähetteelle toimitukseen liittyvä huomio'
          }
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
            anyNote
              ? 'bg-[#FDF6EB] text-[#C05A3C] hover:bg-[#FCE9D2]'
              : 'text-[#6A6D66] hover:bg-[#F4F6F3]'
          }`}
        >
          <StickyNote size={13} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" data-testid={`${testId}-popover`}>
        <div className="px-3 py-2 border-b border-[#E5E6E3]">
          <div className="text-[11px] uppercase tracking-wider text-[#6A6D66] font-semibold">
            Lisätietoja toimitukseen
          </div>
          <div className="text-[10px] text-[#6A6D66] mt-0.5">
            Näkyy lähetteen alaosassa. Tilauskohtainen viesti vain tälle yhdelle päivälle.
          </div>
        </div>
        {hasCustomerNote && (
          <div className="px-3 py-2 border-b border-[#E5E6E3] bg-[#F9F9F6]">
            <div className="text-[10px] uppercase tracking-wider text-[#6A6D66] mb-0.5">
              Asiakkaan vakio-ohje
            </div>
            <div className="text-xs text-[#1C1C1A] whitespace-pre-line">
              {customerNote}
            </div>
          </div>
        )}
        <div className="px-3 py-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid={`${testId}-textarea`}
            placeholder="Esim. Soita ennen tuloa: 040 123 4567"
            rows={3}
            className="w-full text-sm bg-white border border-[#E5E6E3] rounded px-2 py-1.5 focus:outline-none focus:border-[#2A4B30] focus:ring-1 focus:ring-[#2A4B30] resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setDraft(value || ''); setOpen(false); }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
            }}
          />
        </div>
        <div className="px-3 pb-2 flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid={`${testId}-clear`}
            onClick={() => setDraft('')}
            className="text-[11px] text-[#6A6D66] hover:text-[#1C1C1A]"
          >
            Tyhjennä
          </button>
          <button
            type="button"
            data-testid={`${testId}-save`}
            onClick={save}
            className="h-7 px-3 rounded-full bg-[#2A4B30] hover:bg-[#1E3622] text-white text-[11px] font-medium"
          >
            Tallenna
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* --- Main grid --- */

export default function OrderGrid({ rows, reference, onPatch, recentlyTouched, presence, currentClientId, setEditing, currentDate, selectedIds, onToggleSelect, onSelectAllVisible, onClearSelection, sortKey, onToggleSort, onLaheteDone }) {
  const statusOptions = (reference?.status || []).filter(Boolean);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = `${r.customer.ryhma} • ${r.customer.ketju}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return Array.from(m.entries());
  }, [rows]);

  // Build a flat ordered list of customer IDs (respecting group order)
  // and compute next-row map for Enter-advance from one row's Lähete to next row's Määrä
  const nextCustomerId = useMemo(() => {
    const map = {};
    const flat = [];
    for (const [, gr] of grouped) for (const r of gr) flat.push(r.customer.id);
    for (let i = 0; i < flat.length; i++) map[flat[i]] = flat[i + 1] || null;
    return map;
  }, [grouped]);

  const editingByCustomer = useMemo(() => {
    const m = new Map();
    if (!presence) return m;
    for (const u of presence) {
      if (u.client_id === currentClientId) continue;
      if (!u.editing_customer_id) continue;
      if (!m.has(u.editing_customer_id)) m.set(u.editing_customer_id, []);
      m.get(u.editing_customer_id).push(u);
    }
    return m;
  }, [presence, currentClientId]);

  const cellRefs = useRef(new Map());
  const registerCellRef = useCallback((customerId, field) => (ref) => {
    if (!cellRefs.current.has(customerId)) cellRefs.current.set(customerId, {});
    cellRefs.current.get(customerId)[field] = ref;
  }, []);
  const focusField = useCallback((customerId, field) => {
    const fields = cellRefs.current.get(customerId);
    const ref = fields && fields[field];
    const el = ref && ref.current;
    if (el && el.focus) el.focus();
  }, []);

  if (rows.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[#E5E6E3] rounded-xl py-16 text-center">
        <p className="font-heading text-xl text-[#1C1C1A]">Ei rivejä näytettäväksi</p>
        <p className="text-sm text-[#6A6D66] mt-1">Vaihda suodatinta tai lisää aktiivisia asiakkaita.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-[#E5E6E3] bg-white shadow-sm">
      <table className="w-full text-left text-sm">
        <thead className="bg-[#F9F9F6] sticky top-0 z-10 border-b border-[#E5E6E3]">
          <tr className="text-[#6A6D66]">
            <th className="px-2 py-3 w-9">
              {onSelectAllVisible && (
                <SelectAllToggle
                  rows={rows}
                  selectedIds={selectedIds}
                  onSelectAllVisible={onSelectAllVisible}
                  onClearSelection={onClearSelection}
                />
              )}
            </th>
            <th className="px-2 py-3 text-xs font-semibold uppercase tracking-wider w-16 text-center" aria-label="Toiminnot" />
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-24">Ryhmä</th>
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28">Ketju</th>
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">
              {sortKey ? (
                <button
                  type="button"
                  data-testid="order-grid-sort-asiakas"
                  onClick={onToggleSort}
                  className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-[#1C1C1A]"
                  title={sortKey === 'asiakas-asc'
                    ? 'Järjestys: A→Ö (klikkaa kääntääksesi)'
                    : 'Järjestys: Ö→A (klikkaa kääntääksesi)'}
                >
                  Asiakas
                  <span className="text-[10px]">{sortKey === 'asiakas-asc' ? '↑' : '↓'}</span>
                </button>
              ) : (
                <span>Asiakas</span>
              )}
            </th>
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-right w-28">Määrä (kg)</th>
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-32">Status</th>
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28">Lähete</th>
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-40">Ostotilaus</th>
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-32">Reitti</th>
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-20">Nro</th>
            <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-32">Kirjattu</th>
            <th className="px-2 py-3 w-10"></th>
          </tr>
        </thead>
        <tbody>
          {grouped.map(([groupLabel, groupRows]) => (
            <RowGroup
              key={groupLabel}
              label={groupLabel}
              rows={groupRows}
              reference={reference}
              statusOptions={statusOptions}
              onPatch={onPatch}
              recentlyTouched={recentlyTouched}
              editingByCustomer={editingByCustomer}
              setEditing={setEditing}
              currentDate={currentDate}
              registerCellRef={registerCellRef}
              focusField={focusField}
              nextCustomerId={nextCustomerId}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              onLaheteDone={onLaheteDone}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SelectAllToggle({ rows, selectedIds, onSelectAllVisible, onClearSelection }) {
  const filledIds = rows.filter((r) => (r.order?.maara_kg || 0) > 0).map((r) => r.customer.id);
  const allSelected = filledIds.length > 0 && filledIds.every((id) => selectedIds && selectedIds.has(id));
  const someSelected = !allSelected && filledIds.some((id) => selectedIds && selectedIds.has(id));
  return (
    <input
      type="checkbox"
      checked={allSelected}
      ref={(el) => { if (el) el.indeterminate = someSelected; }}
      onChange={() => (allSelected ? onClearSelection() : onSelectAllVisible())}
      data-testid="lahete-select-all-visible"
      title={allSelected ? 'Tyhjennä valinta' : 'Valitse kaikki näkyvät täytetyt'}
      className="h-4 w-4 rounded border-[#A0A39C] cursor-pointer accent-[#2A4B30]"
    />
  );
}

function RowGroup({ label, rows, reference, statusOptions, onPatch, recentlyTouched, editingByCustomer, setEditing, currentDate, registerCellRef, focusField, nextCustomerId, selectedIds, onToggleSelect, onLaheteDone }) {
  return (
    <>
      <tr className="bg-[#F4F6F3] border-y border-[#E5E6E3]">
        <td colSpan={13} className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#2A4B30]">
          {label}
        </td>
      </tr>
      {rows.map((r) => (
        <OrderRow
          key={r.customer.id}
          row={r}
          reference={reference}
          statusOptions={statusOptions}
          onPatch={onPatch}
          recentlyTouched={recentlyTouched}
          editors={editingByCustomer.get(r.customer.id) || []}
          setEditing={setEditing}
          currentDate={currentDate}
          registerCellRef={registerCellRef}
          focusField={focusField}
          nextCustomerId={nextCustomerId[r.customer.id]}
          selected={selectedIds ? selectedIds.has(r.customer.id) : false}
          onToggleSelect={onToggleSelect}
          onLaheteDone={onLaheteDone}
        />
      ))}
    </>
  );
}

function OrderRow({ row, reference, statusOptions, onPatch, recentlyTouched, editors, setEditing, currentDate, registerCellRef, focusField, nextCustomerId, selected, onToggleSelect, onLaheteDone }) {
  const { customer, order } = row;
  const o = order || {};
  const touched = recentlyTouched && recentlyTouched.has(customer.id);
  const kirjattu = o.kirjattu ? new Date(o.kirjattu) : null;
  const isOtherEditing = editors && editors.length > 0;

  const handleEditingChange = (focused) => {
    if (!setEditing) return;
    setEditing(focused ? customer.id : null);
  };

  const advanceFromLahete = () => {
    if (nextCustomerId) focusField(nextCustomerId, 'maara');
  };

  return (
    <tr
      className={`border-b border-[#E5E6E3] hover:bg-[#F9F9F6] transition-colors ${touched ? 'bg-[#FDF6EB]' : ''} ${isOtherEditing ? 'bg-[#FDF6EB]/40' : ''} ${selected ? 'bg-[#F0F6F0]' : ''}`}
      data-testid={`order-row-${customer.id}`}
    >
      <td className="px-2 py-2">
        {(o.maara_kg || 0) > 0 && onToggleSelect ? (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(customer.id)}
            data-testid={`${LAHETE.rowSelectPrefix}${customer.id}`}
            className="h-4 w-4 rounded border-[#A0A39C] cursor-pointer accent-[#2A4B30]"
            title="Valitse lähete-tulostukseen"
          />
        ) : null}
      </td>
      <td className="px-2 py-1 w-16">
        <div className="flex items-center gap-0.5 justify-center">
          {(o.maara_kg || 0) > 0 ? (
            <button
              type="button"
              onClick={async () => {
                try {
                  await downloadPdf(`/api/lahete/single/${currentDate}/${customer.id}.pdf`,
                                    `Lahete_${customer.asiakas}_${currentDate}.pdf`);
                  toast.success(`Lähete tulostettu: ${customer.asiakas}`);
                  if (onLaheteDone) onLaheteDone();
                } catch (e) {
                  toast.error(`Lähete ei onnistunut: ${e.message}`);
                }
              }}
              data-testid={`${LAHETE.rowPrintPrefix}${customer.id}`}
              title="Tulosta lähete"
              className="w-7 h-7 rounded-full hover:bg-[#E8F0E9] text-[#6A6D66] hover:text-[#2A4B30] flex items-center justify-center transition-colors"
            >
              <Printer size={14} />
            </button>
          ) : null}
          <DeliveryNotePopover
            value={o.delivery_note || ''}
            customerNote={customer.delivery_note || ''}
            onCommit={(v) => onPatch(customer.id, { delivery_note: v })}
            testId={`order-delivery-note-${customer.id}`}
            disabled={(o.maara_kg || 0) <= 0}
          />
        </div>
      </td>
      <td className="px-3 py-2 text-[#6A6D66]">{customer.ryhma}</td>
      <td className="px-3 py-2 text-[#6A6D66]">{customer.ketju}</td>
      <td className="px-3 py-2 font-medium text-[#1C1C1A]">
        <div className="flex items-center gap-2">
          <span>{customer.asiakas}</span>
          {isOtherEditing && (
            <span className="flex items-center gap-1 text-[10px] text-[#DDA74F] bg-[#FDF6EB] px-1.5 py-0.5 rounded-full">
              {editors.map((u) => (<PresenceDot key={u.client_id} name={u.name} size={14} />))}
              <span className="ml-0.5">muokkaa</span>
            </span>
          )}
        </div>
      </td>
      <td className="px-1 py-1">
        <InlineNumberCell
          value={o.maara_kg || 0}
          onCommit={(v) => onPatch(customer.id, { maara_kg: v })}
          testId={`${GRID.maaraInputPrefix}${customer.id}`}
          onEditingChange={handleEditingChange}
          onEnterAdvance={() => focusField(customer.id, 'status')}
          registerRef={registerCellRef(customer.id, 'maara')}
        />
      </td>
      <td className="px-1 py-1">
        <StatusAutocompleteCell
          value={o.status || ''}
          onCommit={(v) => onPatch(customer.id, { status: v })}
          testId={`${GRID.statusSelectPrefix}${customer.id}`}
          options={statusOptions}
          onEditingChange={handleEditingChange}
          onEnterAdvance={() => focusField(customer.id, 'lahete')}
          registerRef={registerCellRef(customer.id, 'status')}
        />
      </td>
      <td className="px-1 py-1">
        <LaheteCheckboxCell
          value={o.lahete || ''}
          onCommit={(v) => onPatch(customer.id, { lahete: v })}
          testId={`${GRID.lahetePrefix}${customer.id}`}
          registerRef={registerCellRef(customer.id, 'lahete')}
          onEnterAdvance={advanceFromLahete}
        />
      </td>
      <td className="px-1 py-1">
        <InlineTextCell
          value={o.ostotilaus || ''}
          onCommit={(v) => onPatch(customer.id, { ostotilaus: v })}
          testId={`${GRID.ostotilausPrefix}${customer.id}`}
          onEditingChange={handleEditingChange}
          registerRef={registerCellRef(customer.id, 'ostotilaus')}
        />
      </td>
      <td className="px-2 py-1">
        <Select value={o.reitti || customer.reitti || 'KP'} onValueChange={(v) => onPatch(customer.id, { reitti: v })}>
          <SelectTrigger
            data-testid={`${GRID.reittiSelectPrefix}${customer.id}`}
            className="h-8 border-transparent hover:border-[#E5E6E3] focus:border-[#2A4B30]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(reference?.reitti || []).map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 font-mono text-xs text-[#6A6D66]">{customer.nro}</td>
      <td className="px-3 py-2 text-xs text-[#6A6D66] whitespace-nowrap">
        {kirjattu ? kirjattu.toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
      </td>
      <td className="px-1 py-1 text-right">
        <AuditPopover date={currentDate} customerId={customer.id} />
      </td>
    </tr>
  );
}
